/** Photo + ingredient-group token helpers and body parsing. */

export const PHOTO_TOKEN_RE =
  /\{photo:([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\}/g;

export const IGROUP_TOKEN_RE =
  /\{igroup:([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\}/g;

export const TASTING_TOKEN_RE =
  /\{tasting:([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\}/g;

export const STRUCT_TOKEN_RE =
  /\{(photo|igroup|tasting):([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\}/g;

export function photoToken(mediaUuid) {
  return `{photo:${mediaUuid}}`;
}

export function igroupToken(groupUuid) {
  return `{igroup:${groupUuid}}`;
}

export function tastingToken(tastingUuid) {
  return `{tasting:${tastingUuid}}`;
}

export function listPhotoIdsInBody(body) {
  const ordered = [];
  const seen = new Set();
  const text = String(body || "");
  let match;
  const re = new RegExp(PHOTO_TOKEN_RE.source, "g");
  while ((match = re.exec(text)) !== null) {
    const id = match[1].toLowerCase();
    if (seen.has(id)) continue;
    seen.add(id);
    ordered.push(id);
  }
  return ordered;
}

export function listIgroupIdsInBody(body) {
  const ordered = [];
  const seen = new Set();
  const text = String(body || "");
  let match;
  const re = new RegExp(IGROUP_TOKEN_RE.source, "g");
  while ((match = re.exec(text)) !== null) {
    const id = match[1].toLowerCase();
    if (seen.has(id)) continue;
    seen.add(id);
    ordered.push(id);
  }
  return ordered;
}

/** Strip structural tokens and HTML tags for excerpts / plain text. */
export function getPostText(body) {
  let text = String(body || "").replace(new RegExp(STRUCT_TOKEN_RE.source, "g"), " ");
  text = text.replace(/<[^>]+>/g, " ");
  text = text.replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">");
  return text.replace(/\s+/g, " ").trim();
}

/**
 * Split body into HTML text segments and structural tokens.
 * @returns {{ type: 'html' | 'photo' | 'igroup', value: string }[]}
 */
export function splitBodyForRender(body) {
  const text = String(body || "");
  const parts = [];
  const re = new RegExp(STRUCT_TOKEN_RE.source, "g");
  let lastIndex = 0;
  let match;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: "html", value: text.slice(lastIndex, match.index) });
    }
    parts.push({ type: match[1], value: match[2].toLowerCase() });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push({ type: "html", value: text.slice(lastIndex) });
  }
  return parts;
}

/** Convert legacy markdown bodies into simple HTML for the editor. */
export function legacyBodyToHtml(body) {
  const raw = String(body || "").trim();
  if (!raw) return "<p></p>";
  if (raw.includes("{photo:") || raw.includes("{igroup:") || raw.includes("{tasting:") || /<[a-z][\s\S]*>/i.test(raw)) {
    return raw;
  }
  let md = raw.replace(/^#\s+.+\s*\n+/, "");
  md = md.replace(
    /!\[([^\]]*)\]\(\/media\/([0-9a-fA-F-]{36})\)/g,
    (_, _alt, uuid) => photoToken(uuid)
  );
  const escaped = md
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  const paragraphs = escaped
    .split(/\n{2,}/)
    .map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`)
    .join("");
  return paragraphs || "<p></p>";
}

function escapeAttr(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;");
}

/** Remove ingredient-group nodes and bare tokens from HTML. */
export function stripIgroupContent(html) {
  return String(html || "")
    .replace(
      /<div[^>]*data-type=["']ingredient-group["'][^>]*>[\s\S]*?<\/div>/gi,
      ""
    )
    .replace(new RegExp(IGROUP_TOKEN_RE.source, "g"), "");
}

/**
 * Prepare stored body HTML for TipTap: hydrate photo + ingredient-group + tasting nodes.
 */
export function htmlForEditor(body, photos = [], ingredientGroups = [], tastings = []) {
  const photoMap = Object.fromEntries(
    (photos || []).map((p) => [String(p.media_uuid).toLowerCase(), p])
  );
  const groupMap = Object.fromEntries(
    (ingredientGroups || []).map((g) => [String(g.group_uuid).toLowerCase(), g])
  );
  const tastingMap = Object.fromEntries(
    (tastings || []).map((t) => [String(t.tasting_uuid).toLowerCase(), t])
  );
  let html = legacyBodyToHtml(body);

  // Skip re-wrapping if photo nodes already present without bare tokens
  const hasBarePhotoTokens = /\{photo:/.test(
    html.replace(/<div[^>]*data-type=["']photo["'][^>]*>[\s\S]*?<\/div>/g, "")
  );
  if (hasBarePhotoTokens || !/data-type=["']photo["']/.test(html)) {
    html = html.replace(new RegExp(PHOTO_TOKEN_RE.source, "g"), (_, id) => {
      const photo = photoMap[id.toLowerCase()] || {};
      const desc = escapeAttr(photo.description || "");
      const src = photo.public_url || `/media/${id}`;
      const focus = photo.is_focus ? "true" : "false";
      return `<div data-type="photo" data-media-uuid="${id}" data-src="${src}" data-description="${desc}" data-is-focus="${focus}"></div>`;
    });
  }

  const hydrateIgroup = (id, group) => {
    const g = group || {
      group_uuid: id,
      name: "Ingredients",
      items: [],
    };
    const name = escapeAttr(g.name || "Ingredients");
    const itemsPayload = (g.items || []).map((item) => ({
      ingredient_uuid: item.ingredient_uuid || null,
      ingredient_name: item.ingredient_name || "",
      amount: item.amount,
      unit: item.unit || "gram",
      note: item.note || "",
    }));
    const itemsJson = escapeAttr(JSON.stringify(itemsPayload));
    return `<div data-type="ingredient-group" data-group-uuid="${id}" data-name="${name}" data-items="${itemsJson}"></div>`;
  };

  const hasBareIgroupTokens = /\{igroup:/.test(
    html.replace(/<div[^>]*data-type=["']ingredient-group["'][^>]*>[\s\S]*?<\/div>/g, "")
  );
  if (hasBareIgroupTokens || !/data-type=["']ingredient-group["']/.test(html)) {
    html = html.replace(new RegExp(IGROUP_TOKEN_RE.source, "g"), (_, id) =>
      hydrateIgroup(id, groupMap[id.toLowerCase()])
    );
  }

  const hasBareTastingTokens = /\{tasting:/.test(
    html.replace(/<div[^>]*data-type=["']tasting["'][^>]*>[\s\S]*?<\/div>/g, "")
  );
  if (hasBareTastingTokens || !/data-type=["']tasting["']/.test(html)) {
    html = html.replace(new RegExp(TASTING_TOKEN_RE.source, "g"), (_, id) => {
      const tasting = tastingMap[id.toLowerCase()] || {};
      const title = escapeAttr(tasting.title || "Smagning");
      return `<div data-type="tasting" data-tasting-uuid="${id}" data-title="${title}"></div>`;
    });
  }

  // Legacy recipes: groups stored only in API, not yet as body tokens.
  const referenced = new Set(listIgroupIdsInBody(html));
  const orphans = (ingredientGroups || []).filter(
    (g) => g?.group_uuid && !referenced.has(String(g.group_uuid).toLowerCase())
  );
  if (orphans.length) {
    const orphanHtml = orphans
      .map((g) => hydrateIgroup(String(g.group_uuid), g))
      .join("");
    html = `${html || "<p></p>"}${orphanHtml}`;
  }

  return html || "<p></p>";
}

/**
 * Build magazine sections without floats (photo pairs only; igroups ignored here).
 */
export function buildMagazineSections(body) {
  const parts = splitBodyForRender(body);
  const sections = [];
  let i = 0;
  let photoIndex = 0;
  let pendingHtml = "";

  const flushPendingFull = () => {
    if (pendingHtml.trim()) {
      sections.push({ type: "full", html: pendingHtml });
    }
    pendingHtml = "";
  };

  while (i < parts.length) {
    const part = parts[i];

    if (part.type === "html") {
      pendingHtml += part.value;
      i += 1;
      continue;
    }

    if (part.type === "igroup") {
      flushPendingFull();
      sections.push({ type: "igroup", groupUuid: part.value });
      i += 1;
      continue;
    }

    if (part.type === "tasting") {
      flushPendingFull();
      sections.push({ type: "tasting", tastingUuid: part.value });
      i += 1;
      continue;
    }

    const besideHtml = pendingHtml;
    pendingHtml = "";
    i += 1;

    let belowHtml = "";
    if (parts[i]?.type === "html") {
      belowHtml = parts[i].value;
      i += 1;
    }

    sections.push({
      type: "pair",
      imageSide: photoIndex % 2 === 0 ? "right" : "left",
      mediaUuid: part.value,
      besideHtml,
      belowHtml,
    });
    photoIndex += 1;
  }

  flushPendingFull();
  return sections;
}

export function bodyHasContent(body) {
  if (listPhotoIdsInBody(body).length > 0) return true;
  if (listIgroupIdsInBody(body).length > 0) return true;
  return getPostText(body).length > 0;
}

/** Collect ingredient groups from a TipTap editor document (document order). */
export function extractIngredientGroupsFromEditor(editor) {
  if (!editor) return [];
  const groups = [];
  editor.state.doc.descendants((node) => {
    if (node.type.name !== "ingredientGroup") return;
    let items = [];
    try {
      items = typeof node.attrs.itemsJson === "string"
        ? JSON.parse(node.attrs.itemsJson || "[]")
        : node.attrs.items || [];
    } catch {
      items = [];
    }
    groups.push({
      group_uuid: node.attrs.groupUuid,
      name: node.attrs.name || "",
      items: (items || []).map((item) => ({
        ingredient_uuid: item.ingredient_uuid || undefined,
        ingredient_name: item.ingredient_name || item.name || "",
        amount: item.amount,
        unit: item.unit || "gram",
        note: item.note || null,
      })),
    });
  });
  return groups;
}
